/**
 * Durations and "how long ago", as the product shows them.
 *
 * Each recorder, player and list used to carry its own: one `'00:' + seconds` helper read
 * "00:75" past a minute, and five lists each had their own "3h ago".
 */

const known = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value);
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Seconds as a clock: 9 → "0:09", 75 → "1:15", 3725 → "1:02:05". Hours only when there are any.
 * Not known or negative → "0:00", so a player that has not loaded its length yet still lines up.
 * `{ padMinutes: true }` gives a fixed-width "00:09", for a recorder timer that must not shift.
 */
export function formatDuration(seconds: number | null | undefined, { padMinutes = false }: { padMinutes?: boolean } = {}): string {
	const total = known(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	return h ? `${h}:${pad(m)}:${pad(s)}` : `${padMinutes ? pad(m) : m}:${pad(s)}`;
}

/**
 * "just now", "5m ago", "3h ago", "2d ago", "3w ago", "4mo ago", "1y ago". Never a calendar date,
 * so a list never mixes "24 Sep" with "8w ago". Months are "mo" so they cannot be read as minutes.
 * A date in the future (clock skew) reads "just now". Not a date → "".
 */
export function formatRelative(value: string | number | Date | null | undefined, now: number = Date.now()): string {
	if (value == null || value === '') return '';
	const then = new Date(value).getTime();
	if (Number.isNaN(then)) return '';

	const minutes = Math.max(0, Math.floor((now - then) / 60000));
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	if (days < 30) return `${Math.floor(days / 7)}w ago`;
	if (days < 365) return `${Math.min(11, Math.floor(days / 30))}mo ago`;
	return `${Math.floor(days / 365)}y ago`;
}
